import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useBandwidth } from '../hooks/useBandwidth';
import { usePricing } from '../hooks/usePricing';
import { db } from '../lib/supabase';

const PackageDetailsPage = () => {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { packages, pausePackage, resumePackage, purchasePackage } = useBandwidth();
  const { allTiers, calculatePrice, loading: pricingLoading } = usePricing();

  const [activeTab, setActiveTab] = useState('traffic');
  const [isPausing, setIsPausing] = useState(false);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [firstPurchaseDate, setFirstPurchaseDate] = useState(null);

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState(10);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');

  useEffect(() => {
    const fetchPurchaseHistory = async () => {
      if (packageId) {
        setLoadingHistory(true);
        const { data, error } = await db.purchaseHistory.getByPackage(packageId);
        if (!error && data) {
          setPurchaseHistory(data);
          if (data.length > 0) {
            const oldest = data[data.length - 1];
            setFirstPurchaseDate(oldest.created_at);
          }
        }
        setLoadingHistory(false);
      }
    };
    fetchPurchaseHistory();
  }, [packageId]);

  const pkg = packages.find(p => p.id === packageId);

  if (!pkg) {
    return (
      <div className="package-details-page">
        <div className="breadcrumb">
          <Link to="/dashboard/bandwidth">User Bandwidth Packages</Link>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9,18 15,12 9,6"/>
          </svg>
          <span className="current">View</span>
        </div>
        <h1 className="page-title">Package Details</h1>
        <div className="package-not-found">
          <p>Package not found or has been removed.</p>
          <button onClick={() => navigate('/dashboard/bandwidth')} className="back-btn">
            Back to Packages
          </button>
        </div>
      </div>
    );
  }

  const usagePercent = ((parseFloat(pkg.total_gb) - parseFloat(pkg.remaining_gb)) / parseFloat(pkg.total_gb)) * 100;
  const usedGb = parseFloat(pkg.total_gb) - parseFloat(pkg.remaining_gb);

  const handlePauseResume = async () => {
    setIsPausing(true);
    if (pkg.status === 'active') {
      await pausePackage(pkg.id);
    } else {
      await resumePackage(pkg.id);
    }
    setIsPausing(false);
  };

  const openPurchaseModal = () => {
    setPurchaseAmount(10);
    setPurchaseError('');
    setShowPurchaseModal(true);
  };

  const handlePurchase = async () => {
    if (!pkg) return;

    setIsPurchasing(true);
    setPurchaseError('');

    const result = await purchasePackage(pkg.proxy_type, purchaseAmount);

    if (result.success && result.paymentUrl) {
      setShowPurchaseModal(false);
      window.location.href = result.paymentUrl;
    } else {
      setPurchaseError(result.error || 'Failed to create payment');
      setIsPurchasing(false);
    }
  };

  const getExtendedTiers = (type) => {
    const baseTiers = allTiers[type] || [];
    return baseTiers.filter(tier => tier.minGb > 0);
  };

  const formatTierRange = (tier) => {
    if (tier.maxGb) {
      return `${tier.minGb} - ${tier.maxGb} GB`;
    }
    return `${tier.minGb}+ GB`;
  };

  const isActiveTier = (tier, amount) => {
    if (tier.maxGb) {
      return amount >= tier.minGb && amount <= tier.maxGb;
    }
    return amount >= tier.minGb;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).replace(',', '');
  };

  const getPoolName = (proxyType) => {
    const names = {
      residential_basic: 'RESIDENTIAL BASIC',
      residential_premium: 'RESIDENTIAL PREMIUM',
    };
    return names[proxyType] || proxyType?.toUpperCase();
  };

  const getNetworkType = (proxyType) => {
    if (proxyType?.includes('residential')) return 'RESIDENTIAL';
    if (proxyType?.includes('datacenter')) return 'DATACENTER';
    if (proxyType?.includes('isp')) return 'ISP';
    return 'RESIDENTIAL';
  };

  return (
    <div className="package-details-page">
      <div className="breadcrumb">
        <Link to="/dashboard/bandwidth">User Bandwidth Packages</Link>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9,18 15,12 9,6"/>
        </svg>
        <span className="current">View</span>
      </div>

      <h1 className="page-title">Package Details</h1>

      <div className="package-details-header">
        <div className="package-header-left">
          <div className="package-header-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 3v18h18"/>
              <path d="M18 17V9"/>
              <path d="M13 17V5"/>
              <path d="M8 17v-3"/>
            </svg>
          </div>
          <div className="package-header-info">
            <h2>{pkg.name || getPoolName(pkg.proxy_type)}</h2>
            <div className="package-header-meta">
              <span className="meta-label">PACKAGE DETAILS</span>
              <span className="meta-dot">•</span>
              <span className={`status-badge ${pkg.status}`}>{pkg.status?.toUpperCase()}</span>
            </div>
          </div>
        </div>
        <div className="package-header-actions">
          <button className="add-bandwidth-btn" onClick={openPurchaseModal}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            ADD BANDWIDTH
          </button>
          <button
            className="pause-package-btn"
            onClick={handlePauseResume}
            disabled={isPausing}
          >
            {isPausing ? 'LOADING...' : pkg.status === 'active' ? 'PAUSE PACKAGE' : 'RESUME PACKAGE'}
          </button>
        </div>
      </div>

      <div className="package-stats-grid">
        <div className="package-stat-card">
          <div className="stat-header">
            <span className="stat-label">BANDWIDTH USAGE</span>
            <div className="stat-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 3v18h18"/>
                <path d="M18 17V9"/>
                <path d="M13 17V5"/>
                <path d="M8 17v-3"/>
              </svg>
            </div>
          </div>
          <div className="stat-value-large">
            {usagePercent.toFixed(2)}<span className="stat-unit">%</span>
          </div>
          <div className="usage-progress-bar">
            <div className="progress-fill" style={{ width: `${usagePercent}%` }}></div>
          </div>
          <div className="usage-labels">
            <span>{usedGb.toFixed(2)} GB USED</span>
            <span>TOTAL: {parseFloat(pkg.total_gb).toFixed(2)} GB</span>
          </div>
        </div>

        <div className="package-stat-card">
          <div className="stat-header">
            <span className="stat-label">REMAINING BANDWIDTH</span>
            <div className="stat-icon yellow">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
          </div>
          <div className="stat-value-large">
            {parseFloat(pkg.remaining_gb).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}<span className="stat-unit">GB</span>
          </div>
          <div className="status-indicator">
            <span className={`status-dot ${pkg.status}`}></span>
            <span>STATUS: {pkg.status?.toUpperCase()}</span>
          </div>
        </div>

        <div className="package-stat-card">
          <div className="stat-header">
            <span className="stat-label">TOTAL PACKAGE USAGE</span>
            <div className="stat-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12,6 12,12 16,14"/>
              </svg>
            </div>
          </div>
          <div className="stat-value-large">
            {usedGb.toFixed(2)}<span className="stat-unit">GB</span>
          </div>
          <div className="stat-sublabel">SINCE FIRST PURCHASE</div>
        </div>
      </div>

      <div className="package-details-bottom">
        <div className="package-info-card">
          <div className="info-card-header">
            <div className="info-header-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
            </div>
            <h3>PACKAGE INFORMATION</h3>
          </div>

          <div className="info-rows">
            <div className="info-row">
              <span className="info-label">POOL</span>
              <span className="info-value highlight">{getPoolName(pkg.proxy_type)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">NETWORK TYPE</span>
              <span className="info-value">{getNetworkType(pkg.proxy_type)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">PURCHASE DATE</span>
              <span className="info-value">{formatDate(firstPurchaseDate || pkg.created_at)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">STATUS</span>
              <span className={`info-value status-${pkg.status}`}>{pkg.status?.toUpperCase()}</span>
            </div>
          </div>

        </div>

        <div className="traffic-logs-card">
          <div className="logs-tabs">
            <button
              className={`logs-tab ${activeTab === 'traffic' ? 'active' : ''}`}
              onClick={() => setActiveTab('traffic')}
            >
              TRAFFIC LOGS
            </button>
            <button
              className={`logs-tab ${activeTab === 'purchase' ? 'active' : ''}`}
              onClick={() => setActiveTab('purchase')}
            >
              PURCHASE HISTORY
            </button>
            <button
              className={`logs-tab ${activeTab === 'transfers' ? 'active' : ''}`}
              onClick={() => setActiveTab('transfers')}
            >
              TRANSFERS
            </button>
            <div className="logs-filter">
              <span>LAST 30 DAYS</span>
            </div>
          </div>

          <div className="logs-content">
            {activeTab === 'purchase' ? (
              loadingHistory ? (
                <div className="logs-empty">
                  <p>LOADING...</p>
                </div>
              ) : purchaseHistory.length > 0 ? (
                <div className="purchase-history-list">
                  {purchaseHistory.map((purchase) => (
                    <div key={purchase.id} className="purchase-history-item">
                      <div className="purchase-info">
                        <div className="purchase-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                          </svg>
                        </div>
                        <div className="purchase-details">
                          <span className="purchase-type">{purchase.description || 'BANDWIDTH PURCHASE'}</span>
                          <span className="purchase-date">{formatDate(purchase.created_at)}</span>
                        </div>
                      </div>
                      <div className="purchase-amount">
                        <span className="gb-added">+{parseFloat(purchase.gb_amount).toFixed(2)} GB</span>
                        <span className="price-paid">${parseFloat(purchase.price_paid).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="logs-empty">
                  <div className="empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                  </div>
                  <p>NO PURCHASE HISTORY FOUND</p>
                </div>
              )
            ) : (
              <div className="logs-empty">
                <div className="empty-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                </div>
                <p>{activeTab === 'traffic' ? 'NO USAGE LOGS FOUND' : 'NO TRANSFERS FOUND'}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showPurchaseModal && pkg && (
        <div className="purchase-modal-overlay" onClick={() => setShowPurchaseModal(false)}>
          <div className="purchase-modal" onClick={(e) => e.stopPropagation()}>
            <div className="purchase-modal-header">
              <div className="purchase-header-left">
                <div className="purchase-header-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </div>
                <div className="purchase-header-text">
                  <h3>ADD BANDWIDTH</h3>
                  <p>{getPoolName(pkg.proxy_type)}</p>
                </div>
              </div>
              <button className="purchase-modal-close" onClick={() => setShowPurchaseModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="purchase-amount-section">
              <div className="purchase-amount-header">
                <span className="purchase-amount-label">AMOUNT TO ADD</span>
              </div>
              <div className="purchase-amount-input-wrapper">
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={purchaseAmount}
                  onChange={(e) => setPurchaseAmount(Math.max(1, Math.min(10000, Number(e.target.value) || 1)))}
                  className="purchase-amount-input"
                />
                <span className="purchase-amount-suffix">GB</span>
              </div>
            </div>

            <div className="purchase-pricing-section">
              <span className="purchase-pricing-label">PRICING</span>
              <div className="purchase-pricing-list">
                {pricingLoading ? (
                  <div className="purchase-loading">Loading pricing...</div>
                ) : (
                  getExtendedTiers(pkg.proxy_type).map((tier, index) => (
                    <div
                      key={index}
                      className={`purchase-tier-item ${isActiveTier(tier, purchaseAmount) ? 'active' : ''}`}
                    >
                      <div className="purchase-tier-indicator">
                        <div className="tier-dot"></div>
                      </div>
                      <span className="purchase-tier-range">{formatTierRange(tier)}</span>
                      <span className="purchase-tier-price">${tier.pricePerGb.toFixed(4)}<span>/GB</span></span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="purchase-total-section">
              <div className="purchase-total-left">
                <span className="purchase-total-label">TOTAL COST</span>
                <span className="purchase-total-amount">
                  <span className="currency">$</span>
                  {calculatePrice(pkg.proxy_type, purchaseAmount).totalPrice?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="purchase-total-right">
                <span className="purchase-per-gb">${calculatePrice(pkg.proxy_type, purchaseAmount).pricePerGb?.toFixed(4) || '0.0000'}/GB</span>
              </div>
            </div>

            {purchaseError && (
              <div className="purchase-error">
                {purchaseError}
              </div>
            )}

            <div className="purchase-modal-footer">
              <button
                className="purchase-cancel-btn"
                onClick={() => setShowPurchaseModal(false)}
                disabled={isPurchasing}
              >
                CANCEL
              </button>
              <button
                className="purchase-confirm-btn"
                onClick={handlePurchase}
                disabled={isPurchasing || purchaseAmount < 1}
              >
                {isPurchasing ? 'CREATING PAYMENT...' : 'PAY WITH CRYPTO'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageDetailsPage;
